import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { X, Download, Calendar } from 'lucide-react';
import LoadingSpinner from '../ui/LoadingSpinner';

interface CustomDateRangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDownload: (startDate: string, endDate: string) => void;
  loading: boolean;
}

const CustomDateRangeModal: React.FC<CustomDateRangeModalProps> = ({
  isOpen,
  onClose,
  onDownload,
  loading
}) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [error, setError] = useState('');
  const modalRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      
      gsap.set([overlayRef.current, modalRef.current], { 
        opacity: 0,
        visibility: 'visible'
      });
      
      gsap.set(modalRef.current, { scale: 0.9, y: 50 });
      
      gsap.to(overlayRef.current, {
        opacity: 1,
        duration: 0.2
      });
      
      gsap.to(modalRef.current, {
        opacity: 1,
        scale: 1,
        y: 0,
        duration: 0.3,
        ease: 'power2.out',
        delay: 0.1
      });
    } else {
      document.body.style.overflow = '';
      
      if (modalRef.current && overlayRef.current) {
        gsap.to(modalRef.current, {
          opacity: 0,
          scale: 0.9,
          y: 50,
          duration: 0.2,
          ease: 'power2.in'
        });
        
        gsap.to(overlayRef.current, {
          opacity: 0,
          duration: 0.2,
          delay: 0.1,
          onComplete: () => {
            gsap.set([overlayRef.current, modalRef.current], { 
              visibility: 'hidden' 
            });
          }
        });
      }
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const validateAndSubmit = () => {
    setError('');
    
    if (!startDate || !endDate) {
      setError('Please select both start and end dates.');
      return;
    }
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (start > end) {
      setError('Start date cannot be after end date.');
      return;
    }
    
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    
    if (start > today) {
      setError('Start date cannot be in the future.');
      return;
    }
    
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays > 365) {
      setError('Date range cannot exceed 365 days.');
      return;
    }
    
    onDownload(startDate, endDate);
  };

  const handleClose = () => {
    if (!loading) {
      setStartDate('');
      setEndDate('');
      setError('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Overlay */}
      <div
        ref={overlayRef}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        style={{ visibility: 'hidden' }}
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div
          ref={modalRef}
          className="bg-white dark:bg-dark-800 rounded-xl shadow-2xl border border-gray-200 dark:border-dark-700 w-full max-w-md"
          style={{ visibility: 'hidden' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-700">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
                <Calendar className="w-4 h-4 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Custom Date Range
              </h3>
            </div>
            <button
              onClick={handleClose}
              disabled={loading}
              className="p-1 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-md transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Content */}
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={loading}
                className="input-field"
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={loading}
                className="input-field"
                max={new Date().toISOString().split('T')[0]}
                min={startDate}
              />
            </div>
            
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">
                  {error}
                </p>
              </div>
            )}
            
            <div className="text-sm text-gray-500 dark:text-gray-400">
              <p>• Maximum range: 365 days</p>
              <p>• Dates cannot be in the future</p>
            </div>
          </div>
          
          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-dark-700">
            <button
              onClick={handleClose}
              disabled={loading}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={validateAndSubmit}
              disabled={loading || !startDate || !endDate}
              className="btn-primary"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="small" />
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Download CSV
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomDateRangeModal;
